import React, { useState } from "react";
import styled from "styled-components";
import {
  X,
  Users,
  Shield,
  UserMinus,
  UserPlus,
  Phone,
  Briefcase,
  Loader2,
  Calendar,
} from "lucide-react";
import { StudioRole, StudioRoleMember } from "../../../../Data/role.dto";
import { useAppDispatch } from "../../../../Redux/Hooks";
import {
  unassignRoleAPI,
  getAllRolesAPI,
} from "../../../../Redux/ApiCalls/Dashboard/RoleApi";
import { getAllMembers } from "../../../../Redux/ApiCalls/Dashboard/MembersAPI";
import { showSuccessToast, showErrorToast } from "../../../atoms/Utlis/Toast";
import PermissionGate from "../../../atoms/Utlis/PermissionGate";
import AlertModal from "../../../atoms/Utlis/AlertModal";
import { IMAGE_CDN } from "../../../../config/config";

interface AssignedMembersModalProps {
  isOpen: boolean;
  onClose: () => void;
  role: StudioRole | null;
  onOpenAssignModal?: (roleId: number | string) => void;
}

const gradient =
  "linear-gradient(144.01deg, rgba(255, 201, 81, 0.9) 0%, rgba(206, 86, 176, 0.9) 100%)";

export const AssignedMembersModal: React.FC<AssignedMembersModalProps> = ({
  isOpen,
  onClose,
  role,
  onOpenAssignModal,
}) => {
  const dispatch = useAppDispatch();
  const [unassigningMemberId, setUnassigningMemberId] = useState<
    number | string | null
  >(null);
  const [memberToRemove, setMemberToRemove] = useState<StudioRoleMember | null>(
    null
  );

  if (!isOpen || !role) return null;

  const members = role.assigned_members || [];

  const handleClose = () => {
    setMemberToRemove(null);
    onClose();
  };

  const handleConfirmUnassign = async () => {
    if (!role || !memberToRemove) return;
    const member = memberToRemove;
    setUnassigningMemberId(member.id);
    try {
      await dispatch(
        unassignRoleAPI({
          roleId: role.id,
          teamMemberId: member.id,
        })
      ).unwrap();

      showSuccessToast(`Removed ${member.name} from ${role.name}`);
      setMemberToRemove(null);
      dispatch(getAllRolesAPI());
      dispatch(getAllMembers());
    } catch (err: any) {
      showErrorToast(err?.message || "Failed to unassign role");
    } finally {
      setUnassigningMemberId(null);
    }
  };

  return (
    <Overlay onClick={handleClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <HeaderLeft>
            <IconWrap>
              <Shield size={20} color="#c94daa" />
            </IconWrap>
            <div>
              <ModalTitleRow>
                <ModalTitle>{role.name}</ModalTitle>
                <Badge>{members.length} assigned</Badge>
              </ModalTitleRow>
              <ModalSubtitle>
                {role.description ||
                  `${role.permissions?.length || 0} permissions configured for this role.`}
              </ModalSubtitle>
            </div>
          </HeaderLeft>
          <CloseButton type="button" onClick={handleClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          {members.length === 0 ? (
            <EmptyState>
              <EmptyIconWrap>
                <Users size={32} color="#a3a3a3" />
              </EmptyIconWrap>
              <EmptyTitle>No Members Assigned Yet</EmptyTitle>
              <EmptyText>
                Assign team members to this role so they can log in with phone OTP
                and access features granted by this role.
              </EmptyText>
              {onOpenAssignModal && (
                <PermissionGate permission="role_management.update">
                  <AssignBtn
                    type="button"
                    onClick={() => {
                      handleClose();
                      onOpenAssignModal(role.id);
                    }}
                  >
                    <UserPlus size={15} />
                    <span>Assign First Member</span>
                  </AssignBtn>
                </PermissionGate>
              )}
            </EmptyState>
          ) : (
            <MembersList>
              {members.map((member) => {
                const avatarSrc = member.profile_image
                  ? `${IMAGE_CDN}/${member.profile_image}`
                  : "";
                const isUnassigning = unassigningMemberId === member.id;

                return (
                  <MemberRow key={member.id}>
                    <MemberLeft>
                      <AvatarWrap>
                        {avatarSrc ? (
                          <Avatar src={avatarSrc} alt={member.name} />
                        ) : (
                          <AvatarFallback>
                            {member.name?.charAt(0)?.toUpperCase() || "M"}
                          </AvatarFallback>
                        )}
                      </AvatarWrap>

                      <MemberInfo>
                        <MemberName>{member.name}</MemberName>
                        <MemberMetaRow>
                          {member.phone_number && (
                            <MetaItem>
                              <Phone size={12} />
                              <span>
                                {member.country_code || "+91"}{" "}
                                {member.phone_number}
                              </span>
                            </MetaItem>
                          )}
                          {member.job_type && (
                            <MetaItem>
                              <Briefcase size={12} />
                              <span>
                                {member.job_type}
                                {member.employment_type
                                  ? ` · ${member.employment_type}`
                                  : ""}
                              </span>
                            </MetaItem>
                          )}
                          {member.assigned_at && (
                            <MetaItem>
                              <Calendar size={12} />
                              <span>
                                Assigned{" "}
                                {new Date(
                                  member.assigned_at
                                ).toLocaleDateString()}
                              </span>
                            </MetaItem>
                          )}
                        </MemberMetaRow>
                      </MemberInfo>
                    </MemberLeft>

                    <PermissionGate permission="role_management.update">
                      <UnassignButton
                        type="button"
                        onClick={() => setMemberToRemove(member)}
                        disabled={isUnassigning}
                        title={`Remove ${role.name} from this member`}
                      >
                        {isUnassigning ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <UserMinus size={14} />
                        )}
                        <span>Remove</span>
                      </UnassignButton>
                    </PermissionGate>
                  </MemberRow>
                );
              })}
            </MembersList>
          )}
        </ModalBody>

        <ModalFooter>
          <FooterLeft>
            <FooterCount>
              Showing {members.length} team member
              {members.length !== 1 ? "s" : ""}
            </FooterCount>
          </FooterLeft>
          <FooterActions>
            <CloseModalBtn type="button" onClick={handleClose}>
              Done
            </CloseModalBtn>
            {onOpenAssignModal && (
              <PermissionGate permission="role_management.update">
                <AssignMoreBtn
                  type="button"
                  onClick={() => {
                    handleClose();
                    onOpenAssignModal(role.id);
                  }}
                >
                  <UserPlus size={15} />
                  <span>Assign Another Member</span>
                </AssignMoreBtn>
              </PermissionGate>
            )}
          </FooterActions>
        </ModalFooter>
      </ModalContainer>

      {memberToRemove && (
        <AlertModal
          title="Remove Role"
          badgeText="Role Access"
          customIcon={<UserMinus size={22} />}
          confirmIcon={<UserMinus size={16} />}
          text={
            <>
              Remove role <strong>&ldquo;{role.name}&rdquo;</strong> from{" "}
              <strong>{memberToRemove.name}</strong>?
            </>
          }
          warningText="They will lose permissions associated with this role."
          buttonText="Remove"
          cancel={() => !unassigningMemberId && setMemberToRemove(null)}
          Delete={handleConfirmUnassign}
          isDeleting={unassigningMemberId === memberToRemove.id}
        />
      )}
    </Overlay>
  );
};

export default AssignedMembersModal;

/* Styled Components */

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(2px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  animation: fadeIn 0.15s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContainer = styled.div`
  background: #ffffff;
  border-radius: 20px;
  width: 100%;
  max-width: 620px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 48px rgba(0, 0, 0, 0.16);
  border: 1px solid rgba(23, 23, 23, 0.08);
  overflow: hidden;
  animation: slideUp 0.18s ease-out;

  @keyframes slideUp {
    from {
      transform: translateY(12px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #f0f0f0;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
`;

const IconWrap = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: #fdf2f8;
  border: 1px solid rgba(201, 77, 170, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const ModalTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: #171717;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Badge = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #c94daa;
  background: #fdf2f8;
  border: 1px solid rgba(201, 77, 170, 0.2);
  padding: 2px 8px;
  border-radius: 9999px;
  white-space: nowrap;
`;

const ModalSubtitle = styled.p`
  font-size: 12px;
  color: #737373;
  margin: 2px 0 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 420px;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #737373;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: #f5f5f5;
    color: #171717;
  }
`;

const ModalBody = styled.div`
  padding: 20px 24px;
  overflow-y: auto;
  flex: 1;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 40px 20px;
`;

const EmptyIconWrap = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
`;

const EmptyTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #171717;
  margin: 0 0 6px;
`;

const EmptyText = styled.p`
  font-size: 13px;
  color: #737373;
  margin: 0 0 18px;
  max-width: 380px;
  line-height: 1.5;
`;

const AssignBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 18px;
  border-radius: 10px;
  border: none;
  background: ${gradient};
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 3px 10px rgba(206, 86, 176, 0.25);
  transition: transform 0.15s;

  &:hover {
    transform: translateY(-1px);
  }
`;

const MembersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const MemberRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid #f0f0f0;
  background: #ffffff;
  transition: all 0.15s;

  &:hover {
    border-color: #e5e5e5;
    background: #fafafa;
  }
`;

const MemberLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

const AvatarWrap = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  background: #f5f5f5;
  flex-shrink: 0;
  border: 1px solid rgba(23, 23, 23, 0.08);
`;

const Avatar = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const AvatarFallback = styled.div`
  width: 100%;
  height: 100%;
  background: ${gradient};
  color: #ffffff;
  font-size: 15px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MemberInfo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const MemberName = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: #171717;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MemberMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 3px;
  flex-wrap: wrap;
`;

const MetaItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #737373;
`;

const UnassignButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid #fee2e2;
  background: #ffffff;
  color: #ef4444;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    background: #fef2f2;
    border-color: #fca5a5;
    color: #dc2626;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-top: 1px solid #f0f0f0;
  background: #fafafa;
`;

const FooterLeft = styled.div`
  display: flex;
  align-items: center;
`;

const FooterCount = styled.span`
  font-size: 12px;
  color: #737373;
  font-weight: 500;
`;

const FooterActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CloseModalBtn = styled.button`
  padding: 8px 16px;
  border-radius: 10px;
  border: 1px solid #e5e5e5;
  background: #ffffff;
  font-size: 13px;
  font-weight: 600;
  color: #262626;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: #f5f5f5;
  }
`;

const AssignMoreBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 10px;
  border: none;
  background: ${gradient};
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(206, 86, 176, 0.22);
  transition: transform 0.15s;

  &:hover {
    transform: translateY(-1px);
  }
`;
